import SellerPage from "./seller-page";
import { trackError } from "../utils/error-tracking";
import { asError } from "../hooks/as-error";

/** Columns the seller page needs. Shared by both lookups so they cannot drift. */
const SELLER_SELECT = {
  id: true,
  firstName: true,
  lastName: true,
  username: true,
  trustLevel: true,
  createdDate: true,
  hasPhoto: true,
  updatedDate: true,
  locationText: true,
} as const;

export async function getServerSideProps(context) {
  const slug = context.params.slug;
  const sold = context.query.sold;
  let id;
  let dbUser;

  // The shared server singleton, imported lazily *inside* this server-only
  // function: a top-level import of the Prisma client leaks @prisma/client into
  // this page's client bundle. This page used to build its own client and then
  // `$disconnect()` it in a `finally`, which closed the pool out from under any
  // other request still using it on the same warm lambda and forced the next one
  // to open a fresh pool. Do NOT $disconnect the singleton.
  // eslint-disable-next-line sellwild-code/no-server-import-in-client -- dynamic import inside a getServerSideProps-only helper; never reaches the client bundle
  const { default: prisma } = await import('../lib/prisma');

  try {
    // The username is the address. It is tried first and unconditionally — a name
    // that happens to be all digits is a name, not a row id — which is what lets
    // `4x4trucks` and friends exist at all.
    const users = await prisma.users.findMany({
      where: { username: slug },
      select: SELLER_SELECT,
    });

    // Check for username collisions
    if (users.length > 1) {
      trackError(`Username collision detected for: ${slug}`);
      return { notFound: true };
    }

    if (users.length === 1) {
      dbUser = users[0];
      id = dbUser.id.toString();
    }

    // No current holder — the seller may have renamed. An account keeps the
    // name it moved off in `previousUsername`, and a released name is never
    // re-issued, so this resolves to exactly one account or to nobody.
    // Without this every share, inbound link and indexed result pointing at
    // the old store URL would 404 the moment its owner renamed.
    if (!dbUser) {
      const renamed = await prisma.users.findMany({
        where: { previousUsername: slug },
        select: { username: true },
      });

      if (renamed.length === 1 && renamed[0].username) {
        return {
          // Permanent: the old name cannot come back, so search engines
          // should move their equity to the new URL rather than re-crawl.
          redirect: {
            destination: `/s/${renamed[0].username}`,
            permanent: true,
          },
        };
      }
    }

    // Legacy id URLs. This page used to resolve a numeric slug as a row id, and
    // links minted that way are still out there — in indexed product pages, in
    // the admin dash, in anything anyone shared. They are no longer a way to
    // reach the page; they are forwarded to the seller's real address, the same
    // treatment a released username gets above.
    //
    // The exception is an account that has no username at all: it has no other
    // address to forward to, so its id URL still renders. Roughly forty live
    // sellers are in that state. Backfill them a name and this branch, and the
    // numeric lookup with it, can go entirely.
    if (!dbUser && /^\d+$/.test(slug)) {
      const byId = await prisma.users.findUnique({
        where: { id: Number(slug) },
        select: SELLER_SELECT,
      });

      if (byId?.username) {
        return {
          redirect: { destination: `/s/${byId.username}`, permanent: true },
        };
      }

      if (byId) {
        dbUser = byId;
        id = byId.id.toString();
      }
    }

    if (!dbUser) {
      return { notFound: true };
    }

    const { fetchOtherListings } = await import("../data/api/search");
    const r = await fetchOtherListings(id, null, 1, sold);
    const { listings, user } = r;
    const currentList = listings;

    if (user.trustLevel < 2 || listings.length < 1) {
      return { notFound: true };
    }

    // Get photo URLs
    const { getUserPhotoUrls } = await import('../utils/listing-image-url');
    const photos = dbUser.hasPhoto === 1
      ? getUserPhotoUrls(Number(dbUser.id), dbUser.hasPhoto, dbUser.updatedDate)
      : { url: '', thumbUrl: '' };

    // Build complete user object
    const completeUser = {
      id: dbUser.id.toString(),
      firstName: dbUser.firstName,
      lastName: dbUser.lastName,
      username: dbUser.username,
      trustLevel: dbUser.trustLevel,
      createdDate: dbUser.createdDate.toISOString(),
      photos: dbUser.hasPhoto === 1 ? photos : null, // Use null instead of undefined for serialization
      locationText: dbUser.locationText || null,
    };

    return {
      props: {
        mobileTopNav: "left",
        user: completeUser,
        currentList,
        interceptBack: true,
        fallback: true,
      },
    };
  } catch (e) {
    trackError(asError(e));
    return {
      notFound: true,
    };
  }
}

export default SellerPage;
